import { Trophy, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar, Cell } from "recharts";
import { BAR_RADIUS, DOMAIN_ZERO, TICK, TICK_MD } from "@/lib/chart";
import { TYPES, COLORS, winnerIdx, keyOf } from "@/components/compare/config";

export const Verdict = ({ items, type }) => {
  if (items.length < 2) return null;
  return (
    <div data-testid="compare-verdict" className="grid sm:grid-cols-3 gap-4">
      {TYPES[type].verdict.map((r) => {
        const w = winnerIdx(items, r);
        return (
          <div key={r.l} className="relative grain overflow-hidden rounded-2xl bg-[#1E293B] text-white p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-300 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />{r.l}</p>
            {w >= 0 ? (
              <>
                <p className="font-display font-extrabold text-lg mt-2 leading-tight" style={{ color: COLORS[w] }}>{items[w].name}</p>
                <p className="font-mono text-sm text-slate-300 mt-1">{r.f(items[w])}</p>
              </>
            ) : <p className="text-sm text-slate-400 mt-2">It's a tie / not enough data</p>}
          </div>
        );
      })}
    </div>
  );
};

export const CompareTable = ({ items, type }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
    <table data-testid="compare-table" className="w-full text-sm">
      <thead>
        <tr className="bg-[#F8FAFC]">
          <th className="px-5 py-4 text-left w-52 text-xs uppercase tracking-wider text-slate-500">Parameter</th>
          {items.map((x, i) => <th key={keyOf(x)} className="px-5 py-4 text-left min-w-[200px]"><span className="inline-flex items-center gap-2 font-display font-bold"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i] }} />{x.name}</span></th>)}
        </tr>
      </thead>
      <tbody>
        {TYPES[type].rows.map((r) => {
          const w = winnerIdx(items, r);
          return (
            <tr key={r.l} className="border-t border-slate-100">
              <td className="px-5 py-3.5 font-semibold text-slate-500">{r.l}</td>
              {items.map((x, i) => (
                <td key={keyOf(x)} className={`px-5 py-3.5 ${i === w ? "bg-emerald-50" : ""}`}>
                  <span className={`inline-flex items-center gap-1.5 ${r.v ? "font-mono" : ""} ${i === w ? "font-bold text-[#047857]" : "text-slate-800"}`}>
                    {r.f(x)}{i === w && <Trophy data-testid="compare-winner-icon" className="w-3.5 h-3.5 text-amber-500" />}
                  </span>
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const growthData = (items) => {
  const maps = items.map((f) => Object.fromEntries(f.series.map((p) => [p.date, p.nav])));
  const dates = [...new Set(items.flatMap((f) => f.series.map((p) => p.date)))].sort().filter((d) => maps.every((m) => m[d]));
  return dates.map((d) => ({ date: d, ...Object.fromEntries(items.map((f, i) => [f.code, Math.round((maps[i][d] / maps[i][dates[0]]) * 10000)])) }));
};

export const CompareChart = ({ items, type }) => {
  const t = TYPES[type];
  const metric = t.fund ? null : t.rows.find((r) => r.best);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <p className="font-display font-bold text-lg">{t.fund ? "Growth of ₹10,000 (common period)" : metric.l}</p>
      <div className="h-80 mt-4" data-testid="compare-chart">
        <ResponsiveContainer width="100%" height="100%">
          {t.fund ? (
            <LineChart data={growthData(items)}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(0, 7)} tick={TICK} minTickGap={40} />
              <YAxis tick={TICK} width={60} />
              <Tooltip formatter={(v, k) => [`₹${Number(v).toLocaleString("en-IN")}`, items.find((f) => f.code === k)?.name]} />
              <Legend formatter={(k) => items.find((f) => f.code === k)?.name} />
              {items.map((f, i) => <Line key={f.code} dataKey={f.code} stroke={COLORS[i]} strokeWidth={2.5} dot={false} />)}
            </LineChart>
          ) : (
            <BarChart data={items.map((x) => ({ name: x.name, v: metric.v(x) }))}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" tick={TICK_MD} />
              <YAxis tick={TICK} domain={DOMAIN_ZERO} />
              <Tooltip />
              <Bar dataKey="v" radius={BAR_RADIUS}>{items.map((x, i) => <Cell key={keyOf(x)} fill={COLORS[i]} />)}</Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};
